<template>
  <!-- Hidden until hydrated or when the user has no orgs -->
  <div v-if="!hydrated || orgs.length === 0" class="org-switcher-empty" />

  <!-- Single org: quiet static label, no dropdown (no switcher tax) -->
  <div v-else-if="isSingleOrg" class="org-switcher org-switcher--static d-flex align-center">
    <v-avatar size="26" class="mr-2" :color="orgColor(currentOrg)">
      <span class="org-mono">{{ orgInitials(currentOrg) }}</span>
    </v-avatar>
    <span class="org-name text-truncate">{{ currentOrg?.name }}</span>
  </div>

  <!-- Multi org: switcher menu -->
  <v-menu v-else location="bottom start" :offset="[8, 0]">
    <template #activator="{ props }">
      <v-btn v-bind="props" variant="text" class="org-switcher text-none px-2">
        <v-avatar size="26" class="mr-2" :color="orgColor(currentOrg)">
          <span class="org-mono">{{ orgInitials(currentOrg) }}</span>
        </v-avatar>
        <span class="org-name text-truncate">{{ currentOrg?.name || 'Select organization' }}</span>
        <v-icon size="small" class="ml-1">mdi-chevron-down</v-icon>
      </v-btn>
    </template>

    <v-list density="compact" min-width="280">
      <v-list-subheader>Your organizations</v-list-subheader>

      <v-list-item
        v-for="org in orgs"
        :key="org.id"
        :active="org.id === currentOrgId"
        @click="switchOrg(org.id)"
      >
        <template #prepend>
          <v-avatar size="28" class="mr-2" :color="orgColor(org)">
            <span class="org-mono">{{ orgInitials(org) }}</span>
          </v-avatar>
        </template>

        <v-list-item-title>{{ org.name }}</v-list-item-title>
        <v-list-item-subtitle v-if="projectCount(org.id) !== null">
          {{ projectCount(org.id) }} {{ projectCount(org.id) === 1 ? 'project' : 'projects' }}
        </v-list-item-subtitle>

        <template #append>
          <v-chip
            v-if="roleLabel(org.id)"
            size="x-small"
            :color="roleColor(org.id)"
            variant="tonal"
            class="mr-1"
          >
            {{ roleLabel(org.id) }}
          </v-chip>
          <v-icon v-if="org.id === currentOrgId" size="small" color="primary">mdi-check</v-icon>
        </template>
      </v-list-item>

      <v-divider class="my-1" />

      <v-list-item prepend-icon="mdi-view-dashboard-outline" @click="goAllWorkspaces">
        <v-list-item-title>All workspaces</v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

const store = useStore()
const router = useRouter()

const hydrated = computed(() => store.state.currentOrg.hydrated)
const orgs = computed(() => store.getters['currentOrg/orgs'])
const currentOrg = computed(() => store.getters['currentOrg/currentOrg'])
const currentOrgId = computed(() => store.getters['currentOrg/currentOrgId'])
const isSingleOrg = computed(() => store.getters['currentOrg/isSingleOrg'])
const projectMemberships = computed(() => store.state.projectMemberships.projectMemberships || [])

onMounted(() => {
  if (!store.state.currentOrg.hydrated) {
    store.dispatch('currentOrg/hydrate')
  }
})

const projectCount = (orgId) => {
  const memberships = projectMemberships.value
  if (!memberships.length) {
    return null
  }

  return memberships.filter((m) => m?.project?.organisationId === orgId).length
}

const roleFor = (orgId) => store.getters['orgMembers/getCallerRoleInOrg'](orgId)

const roleLabel = (orgId) => {
  const role = roleFor(orgId)
  if (!role) {
    return ''
  }

  return role.charAt(0) + role.slice(1).toLowerCase()
}

const roleColor = (orgId) => (roleFor(orgId) === 'OWNER' ? 'success' : 'grey')

const orgInitials = (org) => {
  if (!org?.name) {
    return '?'
  }
  const words = org.name.trim().split(/\s+/)
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase()
  }

  return (words[0][0] + words[1][0]).toUpperCase()
}

const COLORS = ['#1867c0', '#6a1b9a', '#2e7d32', '#c62828', '#ef6c00', '#00838f', '#4527a0', '#ad1457']

const orgColor = (org) => {
  if (!org?.id) {
    return 'grey'
  }
  let hash = 0
  for (let i = 0; i < org.id.length; i++) {
    hash = org.id.charCodeAt(i) + ((hash << 5) - hash)
  }

  return COLORS[Math.abs(hash) % COLORS.length]
}

const switchOrg = (orgId) => {
  if (orgId === currentOrgId.value) {
    return
  }
  store.dispatch('currentOrg/setCurrentOrg', { orgId, navigate: true, router })
}

const goAllWorkspaces = () => {
  router.push('/workspaces').catch(() => {})
}
</script>

<style scoped>
.org-switcher {
  max-width: 220px;
  align-self: center;
  margin-left: 20px;
}
.org-switcher--static {
  max-width: 220px;
  padding: 0 8px;
  height: 48px;
  margin-left: 20px;
}
.org-name {
  font-size: 14px;
  font-weight: 500;
  max-width: 150px;
}
.org-mono {
  color: #fff;
  font-size: 12px;
  font-weight: 600;
}
.org-switcher-empty {
  display: none;
}
</style>
